import moment from 'moment';

/**
 * Returns the brand dimension for a markup product, eg:
 *  Single trip => Interjet - Guadalajara:Monterrey
 *  Round trip => ADO:Volaris - Ciudad de México:Acapulco
 *
 * @private
 * @param {Object} purchase A purchase dictionary with the departure and return trips
 * @returns {String}
 */
function ecMarkupBrand({ departs, returns }) {
  const departureTransporter = departs.transporterName;
  const origin = departs.originCityName;
  const destination = departs.destinationCityName;

  if (returns) {
    return `${departureTransporter}:${returns.transporterName} - ${origin}:${destination}`;
  }

  return `${departureTransporter} - ${origin}:${destination}`;
}

/**
 * Returns the product id for an insurance or markup product, eg:
 *  Single trip => insurance - mont-gdlj-24mar170000-1093210-18
 *  Round trip => markup - mont-gdlj-24mar170000-1093210-18:flight-mex_gdl_VW-104_24Mar171755-M35I
 *
 * @private
 * @param {String} productName The type of product for which to generate the id
 * @param {Object} purchase A purchase dictionary with the departure and return trips
 * @returns {String}
 */
function ecExtraProductId(productName, { departs, returns }) {
  if (returns) {
    return `${productName} - ${departs.tripSlug}:${returns.tripSlug}`;
  }

  return `${productName} - ${departs.tripSlug}`;
}

/**
 * Returns the transport type with the first letter in upper case
 * and all the others in lower case
 *  fLiGht => Flight
 *  bus => Bus
 *
 * @param {String} transportType The type of transport: flight | bus
 * @returns {String}
 */
export function normalizeProperty(property) {
  const words = property.split(' ');
  const normalizedWords = words.map((word) => {
    const lowerCaseWord = word.toLowerCase();
    return `${lowerCaseWord.charAt(0).toUpperCase()}${lowerCaseWord.slice(1)}`;
  });

  return normalizedWords.join(' ');
}

/**
 * Returns the selected passengers as described in the analytics metrics model:
 *  { adult: 2, child: 1, infant: 0 } => 2:1:0
 *
 * @param {Object} passengers Dictionary with passenger count by type
 * @returns {String}
 */
export function ecPassengerTypes({ adult = 1, child = 0, infant = 0 }) {
  return `${adult}:${child}:${infant}`;
}

/**
 * Returns the trip product name as described in the analytics metrics model:
 *  departure trip => 'Ida - México:México - Ciudad de México:Acapulco'
 *  return trip => 'Regreso - México:Estados Unidos - Monterrey:Houston'
 *
 * @private
 * @param {Object} trip A trip object as described in the ec.events module (departs | returns)
 * @returns {String}
 */
export function ecTripProductName(trip, way = 'departure') {
  const wayName = way.toLowerCase() === 'return' ? 'Regreso' : 'Ida';
  const originCity = trip.originCityName;
  const originCountry = trip.originCountryName;
  const destinationCity = trip.destinationCityName;
  const destinationCountry = trip.destinationCountryName;

  return `${wayName} - ${originCountry}:${destinationCountry} - ${originCity}:${destinationCity}`;
}

/**
 * Returns one or two formatted Enhanced ecommerce trip products
 * [{
 *   name: 'Ida - Nacional - Ciudad de México:Acapulco',
 *   id: 'mont-gdlj-24mar170000-1093210-18',
 *   passengers: '2:2:1',
 *   price: 150,
 *   brand: 'ADO',
 *   category: 'Bus',
 *   variant: '2017-05-09',
 *   quantity: 1
 * }, {
 *   name: 'Regreso - Nacional - Acapulco:Ciudad de México',
 *   ···
 * }]
 *
 * @param {Object} purchase A purchase dictionary as described in the ec.events module comments
 */
export function ecTripProducts({ departs, returns }) {
  const products = [
    {
      name: ecTripProductName(departs, 'departure'),
      id: departs.tripSlug,
      price: departs.total,
      commision: departs.commission || 0,
      brand: departs.transporterName,
      category: normalizeProperty(departs.transportType),
      variant: moment(departs.departure).format('YYYY-MM-DD'),
      quantity: 1,
    },
  ];

  if (returns) {
    products.push({
      name: ecTripProductName(returns, 'return'),
      id: returns.tripSlug,
      price: returns.total,
      commision: returns.commission || 0,
      brand: returns.transporterName,
      category: normalizeProperty(returns.transportType),
      variant: moment(returns.departure).format('YYYY-MM-DD'),
      quantity: 1,
    });
  }

  return products;
}

/**
 * Returns a formatted Enhanced ecommerce insurance product
 * {
 *   name: 'Insurance',
 *   id: 'insurance - mont-gdlj-24mar170000-1093210-18',
 *   price: 11.6,
 *   brand: 'IRL',
 *   category: 'Extras',
 *   variant: '2017-05-16',
 *   quantity: 1
 * }
 *
 * @param {Object} purchase A purchase dictionary as described in the ec.events module comments
 */
export function ecInsuranceProduct(purchase) {
  const { insuranceAmount, departs } = purchase;

  return {
    name: 'Insurance',
    id: ecExtraProductId('insurance', purchase),
    price: insuranceAmount,
    commission: 0,
    brand: 'IRL',
    category: 'Extras',
    variant: moment(departs.departure).format('YYYY-MM-DD'),
    quantity: 1,
  };
}

/**
 * Returns a formatted Enhanced ecommerce service level product
 * {
 *   name: 'Service Level',
 *   id: 'basic - mont-gdlj-24mar170000-1093210-18',
 *   price: 20,
 *   category: 'Extras',
 *   variant: '2012-05-16',
 *   quantity: 1
 * }
 *
 * @param {Object} purchase A purchase dictionary as described in the ec.events module comments
 */
export function ecServiceLevelProduct(purchase) {
  const { serviceLevel, serviceLevelAmount } = purchase;

  return {
    name: 'Service Level',
    id: ecExtraProductId(serviceLevel.name, purchase),
    price: serviceLevelAmount,
    commission: 0,
    category: 'Extras',
    variant: serviceLevel.name,
    quantity: 1,
  };
}

/**
 * Returns a formatted Enhanced ecommerce markup product
 * {
 *   name: 'Markup',
 *   id: 'markup - mont-gdlj-24mar170000-1093210-18:flight-mex_gdl_VW-104_24Mar171755-M35I',
 *   price: 200,
 *   brand: 'ADO:Volaris - Ciudad de México:Acapulco',
 *   category: 'Extras',
 *   variant: '2017-05-16',
 *   quantity: 1
 * }
 *
 * @param {Object} purchase A purchase dictionary as described in the ec.events module comments
 */
export function ecMarkupProduct(purchase) {
  const { markup, departs } = purchase;

  return {
    name: 'Markup',
    id: ecExtraProductId('markup', purchase),
    price: markup,
    commission: 0,
    brand: ecMarkupBrand(purchase),
    category: 'Extras',
    variant: moment(departs.departure).format('YYYY-MM-DD'),
    quantity: 1,
  };
}

/**
 * Returns the funnel type code:
 * <single|round> <departure transport type>[:<return transport type>] eg:
 *    single b | single bs | single f
 *    round b:b | round b:bs | round bs:b | round bs:bs
 *    round f:f | round f:b | round b:f | round f:bs | round bs:f
 * b: bus, bs: bus with seat selection, f: flight
 *
 * @param {Object} purchase A purchase dictionary as described in the ec.events module comments
 */
export function funnelType({ departs, returns }) {
  const tripType = returns ? 'round' : 'single';
  let departureTransportType = departs.transportType.toLowerCase().charAt(0);
  let returnTransportType = returns
    ? `:${returns.transportType.toLowerCase().charAt(0)}`
    : '';

  if (departureTransportType === 'b' && departs.allowsSeatSelection) {
    departureTransportType += 's';
  }

  if (returnTransportType === ':b' && returns.allowsSeatSelection) {
    returnTransportType += 's';
  }

  return `${tripType} ${departureTransportType}${returnTransportType}`;
}



// WEBPACK FOOTER //
// ./ec.utils.js